import { createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LangContext = createContext({ lang: "en", lp: (p) => p, setLang: () => {} });

export function LangProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = location.pathname.startsWith("/pt")
    ? "pt"
    : location.pathname.startsWith("/nl")
      ? "nl"
      : "en";

  // locale-prefixed internal path: lp("/investors") -> "/pt/investors" when in PT
  const lp = (p) => {
    if (lang === "en") return p;
    const prefix = lang === "pt" ? "/pt" : "/nl";
    return p === "/" ? prefix : `${prefix}${p}`;
  };

  const setLang = (next) => {
    if (next === lang) return;
    const stripped = location.pathname.replace(/^\/(pt|nl)(?=\/|$)/, "") || "/";
    const target = next === "en" ? stripped : `/${next}${stripped === "/" ? "" : stripped}`;
    navigate(target, { scroll: false });
  };

  return <LangContext.Provider value={{ lang, lp, setLang }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);

// Shared route labels (navbar, footer, menus, sitemap)
export const PAGES = {
  "/": { en: "Home", pt: "Início", nl: "Home" },
  "/development-finance-portugal": { en: "Development finance", pt: "Financiamento ao desenvolvimento", nl: "Ontwikkelfinanciering" },
  "/products/pre-construction-finance": { en: "Pre-construction finance", pt: "Financiamento à pré-construção", nl: "Voorbouwfase financiering" },
  "/insights": { en: "Insights", pt: "Insights", nl: "Inzichten" },
  "/apply": { en: "Get indicative terms", pt: "Obter condições indicativas", nl: "Indicatieve voorwaarden" },
  "/brokers": { en: "Brokers", pt: "Intermediários", nl: "Tussenpersonen" },
  "/brokers/submit": { en: "Submit a deal", pt: "Apresentar negócio", nl: "Deal indienen" },
  "/expert-network": { en: "Expert network", pt: "Rede de especialistas", nl: "Expertennetwerk" },
  "/investors": { en: "Investors", pt: "Investidores", nl: "Investeerders" },
  "/about": { en: "About", pt: "Sobre nós", nl: "Over ons" },
  "/contact": { en: "Contact", pt: "Contacto", nl: "Contact" },
  "/book": { en: "Book a meeting", pt: "Marcar reunião", nl: "Afspraak maken" },
  "/faq": { en: "FAQ", pt: "Perguntas frequentes", nl: "Veelgestelde vragen" },
  "/sitemap": { en: "Sitemap", pt: "Mapa do site", nl: "Sitemap" },
  "/privacy": { en: "Privacy policy", pt: "Política de privacidade", nl: "Privacybeleid" },
  "/terms": { en: "Terms of service", pt: "Termos de utilização", nl: "Gebruiksvoorwaarden" },
  "/cookies": { en: "Cookie policy", pt: "Política de cookies", nl: "Cookiebeleid" },
  "/lending-disclaimer": { en: "Lending disclaimer", pt: "Aviso legal sobre crédito", nl: "Kredietdisclaimer" },
};

// Shared chrome strings (navbar + footer)
export const CHROME = {
  en: {
    invest: "I want to Invest",
    financing: "I want Financing",
    book: "Book a Meeting",
    indicative: "Get indicative terms",
    langAria: "Change language",
    en: "English",
    pt: "Português",
    products: "Products",
    company: "Company",
    information: "Information",
    legal: "Legal",
    explore: "Explore",
  },
  pt: {
    invest: "Quero Investir",
    financing: "Quero Financiamento",
    book: "Marcar Reunião",
    indicative: "Obter condições indicativas",
    langAria: "Mudar de idioma",
    en: "English",
    pt: "Português",
    products: "Produtos",
    company: "Empresa",
    information: "Informação",
    legal: "Legal",
    explore: "Explorar",
  },
  nl: {
    invest: "Ik wil investeren",
    financing: "Ik wil financiering",
    book: "Gesprek plannen",
    indicative: "Indicatieve voorwaarden",
    langAria: "Taal wijzigen",
    en: "English",
    pt: "Português",
    nl: "Nederlands",
    products: "Producten",
    company: "Bedrijf",
    information: "Informatie",
    legal: "Legaal",
    explore: "Ontdekken",
  },
};
